import useModal from '@area2k/use-modal';

import ChangePaymentModal from './ChangePaymentModal';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import StackContainer from '@/components/StackContainer';
import { Body, Small, Subheading } from '@/components/Typography';
import useAuth from '@/hooks/useAuth';
import useMediaQuery from '@/hooks/useMediaQuery';
import { GetJobQuery, JobStatusEnum } from '@/types/graphql';
import { costOfJob } from '@/util/job';
import { centsToCurrency, toFixedWithoutZeros } from '@/util/number';

type Props = {
  job: GetJobQuery['job'];
};

const PaymentCard = ({ job }: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const { totalEstimate, totalHours, requiredHours } = costOfJob(job);
  const { currentAdminIsCustomerAdmin } = useAuth();
  const isEditable =
    !currentAdminIsCustomerAdmin && job.status === JobStatusEnum.UPCOMING;
  const cardStyle = {
    width: phoneOnly ? '100%' : '337px',
    height: phoneOnly ? 'auto' : '295px',
    paddingBottom: '16px',
  };
  const [showEditModal, hideEditModal] = useModal(
    () => <ChangePaymentModal hideModal={hideEditModal} job={job} />,
    [job]
  );

  return (
    <Card changeStyle={cardStyle} noRadius={phoneOnly}>
      <Card.Section
        actions={
          isEditable
            ? [
                {
                  a11yLabel: 'Edit payment',
                  label: 'Edit',
                  onAction: () => showEditModal(),
                },
              ]
            : undefined
        }
        styleHeading={true}
        title="Payment"
      >
        <StackContainer css={{ paddingRight: '10px' }}>
          <Stack vertical>
            {!currentAdminIsCustomerAdmin && (
              <Stack justify="apart">
                <Small>{`${
                  job.isHoliday ? 'Holiday' : 'Base'
                } pay rate`}</Small>
                <Body>${centsToCurrency(job.payRate)}</Body>
              </Stack>
            )}
            <Stack justify="apart">
              <Small>{`${job.isHoliday ? 'Holiday' : 'Base'} bill rate`}</Small>
              <Body>${centsToCurrency(job.costRate)}</Body>
            </Stack>
            <Stack justify="apart">
              <Small>Unpaid break time</Small>
              <Body>{`${job.mandatoryBreakTime} min`}</Body>
            </Stack>
            <Stack justify="apart">
              <Small>Shift Length (hours)</Small>
              <Body>{toFixedWithoutZeros(requiredHours, 1)}</Body>
            </Stack>
            <Stack justify="apart">
              <Small>Total Hours</Small>
              <Body>{toFixedWithoutZeros(totalHours, 1)}</Body>
            </Stack>
            <Stack justify="apart">
              <Body>Total estimate</Body>
              <Subheading>${centsToCurrency(totalEstimate)}</Subheading>
            </Stack>
          </Stack>
        </StackContainer>
      </Card.Section>
    </Card>
  );
};

export default PaymentCard;
